import { $t } from '@teachino/shared-i18n';
import {
  type InfrastructureCommand,
  InfrastructureCommandBuilder,
} from '@teachino/shared-infrastructure';
import { useClientHandle, useMutation } from '@urql/vue';
import { computed, ref } from 'vue';
import { useLibraryDomain } from '../useLibraryDomain';
import { changeVisibilityMutation } from './change-visibility.mutation';
import { createPublicStudentLinkMutation } from './create-public-student-link.mutation';
import type { StudentsFacade } from './students.facade';

export class StudentsService implements StudentsFacade {
  private _createPublicStudentLink = useMutation(
    createPublicStudentLinkMutation,
  );

  private client = useClientHandle().client;
  private libraryDomain = useLibraryDomain();

  private _itemId = ref<string | undefined>();
  private _courseId = ref<string | undefined>();

  changeVisibility(args: {
    itemId: string;
    courseId: string;
    isVisible: boolean;
  }): InfrastructureCommand<void> {
    const { itemId, isVisible, courseId } = args;

    const fetching = ref(false);
    const error = ref<{ message: string } | null>(null);

    return {
      run: async () => {
        try {
          await this.client
            .mutation(changeVisibilityMutation, {
              materialId: itemId,
              courseId,
              contentVisibility: isVisible ? 'VISIBLE' : 'HIDDEN',
            })
            .toPromise();

          this.libraryDomain.changeItemVisiblity();
        } catch {
          error.value = { message: $t('generic_error') };
          return Promise.reject($t('generic_error'));
        }
      },

      fetching: computed(() => fetching.value),
      error: computed(() => error.value),
    };
  }

  getPublicItemLink(args: {
    courseId: string;
    itemId: string;
  }): InfrastructureCommand<{
    link: string;
  }> {
    const { courseId, itemId } = args;
    return InfrastructureCommandBuilder.fromMutation(
      this._createPublicStudentLink,
      {
        courseId,
      },
    )
      .withMapper((result) => {
        const link = this.buildPublicLink({
          code: result.createCoursePublicAccess.coursePublicAccess.code,
          materialId: itemId,
        });
        return { link };
      })
      .build();
  }
  getPublicLink(courseId: string): InfrastructureCommand<{
    link: string;
  }> {
    return InfrastructureCommandBuilder.fromMutation(
      this._createPublicStudentLink,
      {
        courseId,
      },
    )
      .withMapper((result) => {
        const link = this.buildPublicLink({
          code: result.createCoursePublicAccess.coursePublicAccess.code,
        });
        return { link };
      })
      .build();
  }

  buildPublicLink(args: { code: string; materialId?: string }): string {
    const { code, materialId } = args;
    const materialQueryParam = materialId ? `?openitem=${materialId}` : '';
    const link = `${document.location.protocol}//${document.location.host}/c/${code}/${materialQueryParam}`;
    return link;
  }
}
